import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMapPin, FiClock, FiExternalLink, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import XceedanceLogo from '../../../assets/images/1682429187974.jpg';
import kasiLogo from '../../../assets/images/kasiLogo.png';
import LBTLogo from '../../../assets/images/LBTLogo.png';
import HeebloLogo from '../../../assets/images/HeebloLogo.jpg';
import EJYLogo from '../../../assets/images/EJYLogo.jpg';

const experiences = [
  {
    id: 'xceedance-l1',
    company: 'Xceedance',
    role: 'Analyst Programmer L1',
    period: 'July 2025 – Present',
    location: 'On-site, Gurugram',
    logo: XceedanceLogo,
    badge: 'Enterprise Claims & FNOL',
    bullets: [
      'Built enterprise claims and FNOL portals using Angular, ASP.NET Core, Entity Framework Core, SQL, and REST APIs, reducing manual FNOL processing by 70% through workflow automation.',
      'Built 15+ reusable Angular components, reactive forms, dashboards, and secure backend services following CQRS and Clean Architecture.',
      'Drove the successful implementation of two major feature sets on the Claims platform, directly contributing to a 10% reduction in average claim processing time.'
    ],
    techStack: ['Angular', 'ASP.NET Core', 'EF Core', 'CQRS', 'Clean Architecture', 'SQL', 'REST APIs'],
    links: [
      { label: 'Xceedance Website', url: 'https://xceedance.com/' }
    ]
  },
  {
    id: 'xceedance-intern',
    company: 'Xceedance',
    role: 'Associate Programmer Intern',
    period: 'Jan 2025 – June 2025',
    location: 'On-site, Gurugram',
    logo: XceedanceLogo,
    badge: 'Broker Portal MVP',
    bullets: [
      'Developed MVP Broker Portal for commercial insurance, showcasing quote generation, policy workflows, and secure authentication to multiple potential clients.',
      'Engineered robust backend services with Entity Framework, applying CQRS principles to achieve clean separation of concerns and boost data operation scalability by 20%.',
      'Collaborated closely with product owners to build robust API endpoints, handle business logic, and integrate secure JWT-based authentication mechanisms.'
    ],
    techStack: ['Angular', '.NET Core', 'CQRS', 'Entity Framework', 'JWT Auth', 'REST APIs'],
    links: [
      { label: 'Xceedance Website', url: 'https://xceedance.com/' }
    ]
  },
  {
    id: 'kasi',
    company: 'Kasi Care',
    role: 'Software Engineering Intern',
    period: 'July 2024 – Oct 2024',
    location: 'Remote',
    logo: kasiLogo,
    badge: 'Healthcare & Chrome Ext.',
    bullets: [
      'Built healthcare modules supporting appointment scheduling, payment processing, and therapy session management.',
      'Developed a Chrome Extension (Manifest V3) to automate consultation notes and reduce manual documentation.',
      'Integrated Razorpay, REST APIs, and authentication to deliver production-ready healthcare solutions.'
    ],
    techStack: ['React', 'Node.js', 'Chrome Ext (V3)', 'Razorpay', 'REST APIs'],
    links: [
      { label: 'Website', url: 'https://kasi.care' },
      { label: 'For Therapists (PracFlow)', url: 'https://pracflow.ai' }
    ]
  },
  {
    id: 'lbt',
    company: "Let's Bunk Today",
    role: 'Founder & Community Manager',
    period: 'Nov 2022 - Present',
    location: 'Remote',
    logo: LBTLogo,
    badge: '15,000+ Visitors',
    bullets: [
      'Established and led the thriving "Let\'s Bunk Today" community, attracting over 15,000+ visitors to date.',
      'Curated and managed a comprehensive collection of 1000+ resources spanning various domains.',
      'Received positive feedback from community members, acknowledging platform value and impact.'
    ],
    techStack: ['React', 'Tailwind CSS', 'Resource Hub', 'Community Growth'],
    links: [
      { label: 'Website', url: 'https://letsbunktoday.tech' },
      { label: 'Join Community', url: 'https://chat.whatsapp.com/ERbwoQH9JqZG4l52KC8feU' },
      { label: 'LinkedIn', url: 'https://linkedin.com/company/let-s-bunk-today/' },
      { label: 'Instagram', url: 'https://www.instagram.com/lets_bunk_today/' }
    ]
  },
  {
    id: 'heeblo',
    company: 'Heeblo',
    role: 'UI/UX Designer',
    period: 'Aug 2023 - Sept 2023 (1 Month)',
    location: 'Remote',
    logo: HeebloLogo,
    badge: '15+ Screens',
    bullets: [
      'Successfully created 15+ intuitive UI designs for multiple screens in a month.',
      'Done wireframing, mockups, prototyping to understand user experience.',
      'Analyzed various requirements to ensure project was delivered to highest standards.'
    ],
    techStack: ['Figma', 'UI/UX', 'Wireframing', 'Prototyping', 'User Research'],
    links: [
      { label: 'Design Work (Figma)', url: 'https://www.figma.com/design/ohrez85qOgqzPJwGeVZRuT/Heeblo-Design?node-id=19-219&t=IzUSzIpSuJEJp95Y-1' },
      { label: 'Certificate', url: 'https://drive.google.com/file/d/1OMcdaevpx7ogwku-T3xy_bxKg9pOzgOQ/view?usp=drive_link' }
    ]
  },
  {
    id: 'ejy',
    company: 'Ejy Health',
    role: 'UI/UX Designer',
    period: 'Aug 2022 - Dec 2022 (5 Months)',
    location: 'Remote',
    logo: EJYLogo,
    badge: 'HealthTech Design',
    bullets: [
      'Responsible for overseeing design aspect of projects, ensuring team efficiency.',
      'Designed wireframes of 10+ Screens for official website of EJY Health to improve user experience.',
      'Researched various requirements to ensure all projects were delivered to highest standards.'
    ],
    techStack: ['Figma', 'Wireframing', 'Information Architecture', 'Usability Testing'],
    links: [
      { label: 'Certificate', url: 'https://credential.certifyme.online/verify/8f2a31a03004' },
      { label: 'Letter of Recomm.', url: 'https://drive.google.com/file/d/1MGAY9x28skBXn8iUQIjVKp6ublAYYuOl/view?usp=sharing' },
      { label: 'Research Work', url: 'https://drive.google.com/drive/folders/1J4WnqCQny0T5Fj9SZjuB2xj7QhxW7uxS?usp=sharing' }
    ]
  }
];

const Experience = ({ id }) => {
  const [activeTab, setActiveTab] = useState(experiences[0].id);

  const selectedExp = experiences.find(e => e.id === activeTab) || experiences[0];

  return (
    <div id={id} className="bg-neutral-50 py-12 px-4 border-t border-slate-200">
      
      {/* Title matching site convention */}
      <p className="text-4xl text-black py-4 px-2 flex justify-center m-auto lg:text-5xl font-bold mb-8">
        My <span className="name">&nbsp;Experience</span>
      </p>

      <div className="mx-auto max-w-6xl rounded-2xl border border-slate-300 bg-white overflow-hidden grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-300">
        
        {/* Left Column: Company Selector */}
        <div className="lg:col-span-4 flex flex-col divide-y divide-slate-200 bg-slate-50/50">
          {experiences.map((exp) => {
            const isSelected = exp.id === activeTab;
            return (
              <button
                key={exp.id}
                onClick={() => setActiveTab(exp.id)}
                className={`p-4 sm:p-5 flex items-center justify-between text-left transition-colors duration-200 cursor-pointer ${
                  isSelected
                    ? 'bg-slate-900 text-white font-bold'
                    : 'bg-transparent text-slate-800 hover:bg-slate-100 font-medium'
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <img
                    src={exp.logo}
                    alt={exp.company}
                    className="w-10 h-10 rounded-lg object-cover bg-white border border-slate-300"
                  />
                  <div>
                    <h4 className="text-sm font-extrabold tracking-tight">
                      {exp.company}
                    </h4>
                    <p className={`text-xs mt-0.5 ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                      {exp.role}
                    </p>
                  </div>
                </div>

                <FiArrowRight className={`text-base transition-transform ${isSelected ? 'text-white translate-x-1' : 'text-slate-400'}`} />
              </button>
            );
          })}
        </div>

        {/* Right Column: Experience Details */}
        <div className="lg:col-span-8 p-6 sm:p-8 bg-white text-slate-900 flex flex-col justify-between">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedExp.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* Header Bar */}
              <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <img
                    src={selectedExp.logo}
                    alt={selectedExp.company}
                    className="w-12 h-12 rounded-xl object-cover border border-slate-300 p-0.5 bg-white"
                  />
                  <div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                      {selectedExp.role}
                    </h3>
                    <p className="text-sm font-bold text-indigo-600 mt-0.5">
                      {selectedExp.company}
                    </p>
                  </div>
                </div>

                <span className="px-3.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-800 border border-slate-300">
                  {selectedExp.badge}
                </span>
              </div>

              {/* Period & Location Pills */}
              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-600">
                <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                  <FiClock className="text-slate-800 text-sm" />
                  <span>{selectedExp.period}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                  <FiMapPin className="text-slate-800 text-sm" />
                  <span>{selectedExp.location}</span>
                </div>
              </div>

              {/* Bullets */}
              <div className="space-y-3.5">
                {selectedExp.bullets.map((bullet, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed font-medium">
                    <FiCheckCircle className="text-indigo-600 text-base mt-0.5 shrink-0" />
                    <span>{bullet}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack Pills */}
              <div className="pt-2">
                <h5 className="text-xs uppercase font-mono font-bold text-slate-400 mb-3 tracking-wider">
                  Technologies Used
                </h5>
                <div className="flex flex-wrap gap-2">
                  {selectedExp.techStack.map((tech, tIdx) => (
                    <span
                      key={tIdx}
                      className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 border border-slate-200 text-slate-800"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Links */}
              {selectedExp.links && selectedExp.links.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-200">
                  {selectedExp.links.map((link, lIdx) => (
                    <a
                      key={lIdx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-slate-900 hover:bg-indigo-600 text-white transition-all"
                    >
                      <span>{link.label}</span>
                      <FiExternalLink />
                    </a>
                  ))}
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>

      </div>

    </div>
  );
};

export default Experience;





